// Central tunables. Tweak here to rebalance feel & pacing.

export const CONFIG = {
  world: {
    width: 3200,
    height: 3200,
  },

  // Fixed-timestep simulation.
  sim: {
    dt: 1 / 60,
    maxSubSteps: 5,
  },

  player: {
    baseRadius: 16, // radius at realm 0
    baseSpeed: 300, // px/s at realm 0
    speedPerStage: -5, // px/s lost per realm (bigger = statelier)
    minSpeed: 215,
    accel: 12,
    eatReach: 6,
  },

  // Enlightenment (XP) curve: xpToNext = round(base * growth^stage)
  progression: {
    base: 10,
    growth: 1.5,
  },

  // ── The Three Poisons ──────────────────────────────────────────────
  // These are the skill ceiling. Each poison works AGAINST mindless play.
  poisons: {
    // Ignorance → mindful pacing. Gorging (eating too fast) is punished.
    mindfulWindow: 0.75, // s; a bite sooner than this after the last is "greedy"
    greedyFactor: 0.3, // greedy bites are worth only this fraction
    mindfulGainPerBite: 0.18, // mindfulness multiplier growth on a paced bite
    mindfulMax: 4, // multiplier cap
    mindfulGreedPenalty: 0.55, // multiply the multiplier by this on a greedy bite
    breathPeriod: 2.6, // s per breath cycle (the cadence to eat on)
    breathWindow: 0.4, // s around the breath peak that counts as "on-beat"
    breathBonusGain: 0.22, // extra mindfulness growth for an on-beat bite

    // Attachment → variety. Eating the same source repeatedly decays value.
    attachmentPenalty: 0.22, // -22% value per consecutive same-type bite
    attachmentFloor: 0.25, // value never drops below this fraction from repetition
    varietyGainPerNew: 0.12, // mindfulness growth for eating a *new* type
  },

  // Aversion → fullness. You can only hold so much before you must cleanse.
  fullness: {
    max: 100,
    perBite: 8, // base defilement added per bite (scaled by poop size)
    perBitePerStage: 0.6, // +fill per realm (higher realms fill faster)
    slowThreshold: 0.7, // fraction above which you slow + score less
    slowSpeedFactor: 0.62, // speed multiplier when overfull
    overfullScoreFactor: 0.5, // score multiplier when overfull
    clarityDuration: 6, // s of "clarity" buff after a successful cleanse
    clarityScoreFactor: 1.6, // score multiplier during clarity
  },

  // Predators → the fail state. Beings above your realm hunt you.
  predators: {
    // A being is a predator if its threat tier > player's realm + safeMargin.
    // Base beings spawn within this margin (so they're food, not threats);
    // predators arrive via evolve "waves" and are outgrown as you level up.
    safeMargin: 2,
    senseRadius: 360, // starts chasing within this distance
    baseChaseSpeed: 116,
    chaseSpeedPerThreat: 12, // faster the higher its tier
    chaseSpeedPerStage: 8, // and faster in higher realms
    eatOverlap: 0.7, // fraction of radii sum that counts as "caught"
  },

  poop: {
    baseValue: 1,
    baseRadius: 8,
    maxOnMap: 240,
    lifetime: 24,
  },

  beings: {
    count: 30,
    poopIntervalMin: 1.4,
    poopIntervalMax: 4.5,
    wanderSpeedMin: 30,
    wanderSpeedMax: 90,
  },

  buddha: {
    speed: 72,
    goldenIntervalMin: 6,
    goldenIntervalMax: 11,
  },

  // The cleansing meditation: TILT to guide the light around the mandala and
  // fully complete the loop before the incense burns out. Only a full trace
  // cleanses — and every cleanse is a level harder than the last.
  mandala: {
    basePetals: 4, // lobes in the path at level 0
    petalsPerLevel: 1,
    maxPetals: 10,
    baseTolerance: 34, // px off-path allowed at level 0
    tolerancePerLevel: -2.4, // tighter each cleanse
    minTolerance: 14,
    baseTimeLimit: 13, // s of incense at level 0 (you set your own pace)
    timePerLevel: -0.5, // less each cleanse
    minTimeLimit: 6,
    tiltSensitivityDeg: 26, // degrees of tilt for full-screen cursor deflection
  },

  camera: {
    baseZoom: 1.45,
    zoomPerStage: -0.07,
    minZoom: 0.72,
    follow: 6,
  },
} as const;

export type Config = typeof CONFIG;
